/**
 * Pending-attempt хуки для вкладки «Переменные и скидка»: один frozen
 * pending body на subject (значение переменной — tenant+admin+userId+
 * definitionId; скидка — tenant+admin+userId; создание определения —
 * tenant+admin+key), переживающий unmount/logout. 409 никогда не
 * авто-перезаписывается: только фреш-GET для отображения и ЯВНОЕ новое
 * действие админа с актуальной версией. См. аудит
 * D:\claude dev\work\dlvry-zhorka-audit-20261017 — GET после обрыва
 * НИКОГДА не доказывает отсутствие записи, поэтому локальный маркер
 * чистится только по позитивному ACK (полному known-contract эху), а при
 * конфликте — только когда фреш-версия строго больше замороженной
 * expected_version.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  getPersonalDiscount, listVariableDefinitions, listClientVariableValues,
  setClientVariableValue, setPersonalDiscount, createVariableDefinition,
} from '../services/customerCareApi';
import type { ClientVariableDefinition, ClientVariableValueType } from '../services/customerCareApi';
import type { useScenarioScope, ScenarioScopeToken } from '../pages/marketing/useScenarioScope';
import {
  buildDefinitionCreateMarker, buildDiscountMarker, buildValueMarker,
  definitionCreateLockName, definitionCreateMarkersMatch, definitionCreateStorageKey,
  discountLockName, discountMarkersMatch, discountStorageKey,
  isWebLocksAvailable, notifyClientVarAttemptChanged, CLIENT_VAR_ATTEMPT_EVENT,
  readStoredDefinitionCreateAttempt, readStoredDiscountAttempt, readStoredValueAttempt,
  removeStoredDefinitionCreateAttempt, removeStoredDiscountAttempt, removeStoredValueAttempt,
  valueLockName, valueMarkersMatch, valueStorageKey,
  writeStoredDefinitionCreateAttempt, writeStoredDiscountAttempt, writeStoredValueAttempt,
} from './clientVariableAttemptStorage';
import type {
  StoredDefinitionCreateBody, StoredDefinitionCreateMarker,
  StoredDiscountBody, StoredDiscountMarker,
  StoredVariableValueBody, StoredVariableValueMarker,
} from './clientVariableAttemptStorage';

type ScenarioScope = ReturnType<typeof useScenarioScope>;

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function isPositiveSafeInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value > 0;
}
function isNonNegativeSafeInt(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0;
}
function isTypedScalar(value: unknown): value is string | number | boolean {
  if (typeof value === 'string' || typeof value === 'boolean') return true;
  return typeof value === 'number' && Number.isFinite(value);
}

export interface VariableValueRow {
  definitionId: number;
  key: string;
  value: string | number | boolean;
  version: number;
}

/** Строгий парсер строки GET /admin/clients/{id}/variables — присутствующая
 * строка без корректной версии — это ОШИБКА, а не версия 0. */
export function parseVariableValueRow(raw: unknown): VariableValueRow | null {
  if (!isPlainObject(raw)) return null;
  const { definition_id: definitionId, key, value, version } = raw;
  if (!isPositiveSafeInt(definitionId) || typeof key !== 'string' || !isNonNegativeSafeInt(version)) return null;
  if (!isPlainObject(value) || !isTypedScalar(value.v)) return null;
  return { definitionId, key, value: value.v, version };
}

export interface PersonalDiscountSnapshot {
  personalDiscount: number | null;
  version: number;
}

export function parsePersonalDiscount(raw: unknown): PersonalDiscountSnapshot | null {
  if (!isPlainObject(raw)) return null;
  const { personal_discount: personalDiscount, version } = raw;
  if (!isNonNegativeSafeInt(version)) return null;
  if (personalDiscount === null) return { personalDiscount: null, version };
  if (typeof personalDiscount !== 'number') return null;
  if (!(Number.isSafeInteger(personalDiscount) && personalDiscount >= 0 && personalDiscount <= 100)) return null;
  return { personalDiscount, version };
}

export function parsePersonalDiscountWriteAck(raw: unknown): PersonalDiscountSnapshot | null {
  if (!isPlainObject(raw) || raw.ok !== true) return null;
  return parsePersonalDiscount(raw);
}

export interface DefinitionEcho {
  key: string;
  label: string;
  valueType: ClientVariableValueType;
  enumChoices: string[] | null;
}

const VALUE_TYPES = new Set<ClientVariableValueType>(['string', 'int', 'bool', 'date', 'enum']);

export function parseDefinitionEcho(raw: unknown): DefinitionEcho | null {
  if (!isPlainObject(raw)) return null;
  const { key, label, value_type: valueType, enum_choices: enumChoices } = raw;
  if (typeof key !== 'string' || typeof label !== 'string') return null;
  if (typeof valueType !== 'string' || !VALUE_TYPES.has(valueType as ClientVariableValueType)) return null;
  if (enumChoices !== null && !(Array.isArray(enumChoices) && enumChoices.every((c) => typeof c === 'string'))) return null;
  return { key, label, valueType: valueType as ClientVariableValueType, enumChoices: (enumChoices as string[] | null) };
}

export function enumChoicesEqual(a: string[] | null, b: string[] | null): boolean {
  if (a === null || b === null) return a === b;
  if (a.length !== b.length) return false;
  return a.every((v, i) => v === b[i]);
}

function environmentReady(): boolean {
  return isWebLocksAvailable();
}

// ── Значение переменной клиента (PUT) ──────────────────────────

export type ValueAttemptStatus = 'ready' | 'pending' | 'submitting' | 'blocked' | 'conflict' | 'success';

export interface ValueAttemptState {
  status: ValueAttemptStatus;
  pendingBody: StoredVariableValueBody | null;
  success: VariableValueRow | null;
  conflictValue: VariableValueRow | null;
  errorCode: string | null;
}

const EMPTY_VALUE_STATE: ValueAttemptState = { status: 'ready', pendingBody: null, success: null, conflictValue: null, errorCode: null };

export function useVariableValueAttempt(scope: ScenarioScope, userId: number, definitionId: number) {
  const computeSnapshot = useCallback((): ValueAttemptState => {
    const token = scope.capture();
    if (!scope.enabled || !token.tenantId || !token.adminId || !userId || !definitionId) return EMPTY_VALUE_STATE;
    if (!environmentReady()) return { ...EMPTY_VALUE_STATE, status: 'blocked', errorCode: 'storage_unavailable' };
    const read = readStoredValueAttempt(token.tenantId, token.adminId, userId, definitionId);
    if (read.status === 'empty') return EMPTY_VALUE_STATE;
    if (read.status === 'valid') return { ...EMPTY_VALUE_STATE, status: 'pending', pendingBody: JSON.parse(read.marker.bodyJson) };
    return { ...EMPTY_VALUE_STATE, status: 'blocked', errorCode: read.status === 'storage_error' ? 'storage_unavailable' : 'corrupt_marker' };
  }, [scope, userId, definitionId]);

  const [state, setState] = useState<ValueAttemptState>(() => computeSnapshot());
  const lastRef = useRef<{ scope: ScenarioScope; userId: number; definitionId: number }>({ scope, userId, definitionId });
  let effective = state;
  if (lastRef.current.scope !== scope || lastRef.current.userId !== userId || lastRef.current.definitionId !== definitionId) {
    lastRef.current = { scope, userId, definitionId };
    effective = computeSnapshot();
    setState(effective);
  }

  const busyRef = useRef<ScenarioScopeToken | null>(null);

  useEffect(() => {
    const token = scope.capture();
    if (!scope.enabled || !token.tenantId || !token.adminId || !userId || !definitionId) return undefined;
    const tenantId = token.tenantId, adminId = token.adminId;
    const expectedKey = valueStorageKey(tenantId, adminId, userId, definitionId);
    const refresh = () => {
      if (!scope.isCurrent(token)) return;
      setState((prev) => (prev.status === 'submitting' ? prev : computeSnapshot()));
    };
    const onStorage = (e: StorageEvent) => { if (e.key === null || e.key === expectedKey) refresh(); };
    const onCustom = (e: Event) => {
      const d = (e as CustomEvent<Record<string, unknown>>).detail;
      if (d && d.kind === 'value' && d.tenantId === tenantId && d.adminId === adminId && d.userId === userId && d.definitionId === definitionId) refresh();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(CLIENT_VAR_ATTEMPT_EVENT, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CLIENT_VAR_ATTEMPT_EVENT, onCustom);
    };
  }, [scope, userId, definitionId, computeSnapshot]);

  const handleConflict = useCallback(async (token: ScenarioScopeToken, marker: StoredVariableValueMarker): Promise<void> => {
    let rows;
    try {
      const requestConfig = scope.requestConfig(token);
      rows = await listClientVariableValues(userId, requestConfig);
    } catch {
      if (scope.isCurrent(token)) setState({ status: 'pending', pendingBody: JSON.parse(marker.bodyJson), success: null, conflictValue: null, errorCode: 'unknown_retry_needed' });
      return;
    }
    if (!scope.isCurrent(token)) return;
    const fresh = rows.map(parseVariableValueRow).find((r) => r && r.definitionId === marker.definitionId) ?? null;
    // Не найдено или не строго больше замороженной версии — ничего не
    // доказано ("не увидел" != "не будет"): маркер остаётся pending.
    if (!fresh || fresh.version <= marker.expectedVersion) {
      setState({ status: 'pending', pendingBody: JSON.parse(marker.bodyJson), success: null, conflictValue: null, errorCode: 'unknown_retry_needed' });
      return;
    }
    try {
      await navigator.locks.request(valueLockName(marker.tenantId, marker.adminId, marker.userId, marker.definitionId), async () => {
        if (!scope.isCurrent(token)) return;
        const current = readStoredValueAttempt(marker.tenantId, marker.adminId, marker.userId, marker.definitionId);
        if (current.status === 'valid' && valueMarkersMatch(current.marker, marker)) {
          removeStoredValueAttempt(marker.tenantId, marker.adminId, marker.userId, marker.definitionId);
        }
      });
    } catch { /* best-effort; сервер — источник истины */ }
    notifyClientVarAttemptChanged({ kind: 'value', tenantId: marker.tenantId, adminId: marker.adminId, userId: marker.userId, definitionId: marker.definitionId });
    if (scope.isCurrent(token)) setState({ status: 'conflict', pendingBody: null, success: null, conflictValue: fresh, errorCode: null });
  }, [scope, userId]);

  const submit = useCallback(async (token: ScenarioScopeToken, marker: StoredVariableValueMarker): Promise<void> => {
    let response;
    try {
      const requestConfig = scope.requestConfig(token);
      const body = JSON.parse(marker.bodyJson) as StoredVariableValueBody;
      response = await setClientVariableValue(userId, marker.definitionId, body.value, body.expected_version, requestConfig);
    } catch (error) {
      if (!scope.isCurrent(token)) return;
      if (axios.isAxiosError(error) && error.response?.status === 409) { await handleConflict(token, marker); return; }
      setState({ status: 'pending', pendingBody: JSON.parse(marker.bodyJson), success: null, conflictValue: null, errorCode: 'unknown_retry_needed' });
      return;
    }
    if (!scope.isCurrent(token)) return;
    const requestBody = JSON.parse(marker.bodyJson) as StoredVariableValueBody;
    const parsed = parseVariableValueRow(response);
    // Полное known-contract эхо: definition_id замороженного маркера,
    // версия РОВНО expectedVersion+1, значение буквально равно запрошенному
    // (включая 0/false/'').
    const ackValid = parsed !== null && parsed.definitionId === marker.definitionId
      && parsed.version === marker.expectedVersion + 1 && parsed.value === requestBody.value;
    if (!ackValid) {
      setState({ status: 'pending', pendingBody: requestBody, success: null, conflictValue: null, errorCode: 'invalid_response' });
      return;
    }
    try {
      await navigator.locks.request(valueLockName(marker.tenantId, marker.adminId, marker.userId, marker.definitionId), async () => {
        if (!scope.isCurrent(token)) return;
        const current = readStoredValueAttempt(marker.tenantId, marker.adminId, marker.userId, marker.definitionId);
        if (current.status === 'valid' && valueMarkersMatch(current.marker, marker)) {
          removeStoredValueAttempt(marker.tenantId, marker.adminId, marker.userId, marker.definitionId);
        }
      });
    } catch { /* best-effort */ }
    notifyClientVarAttemptChanged({ kind: 'value', tenantId: marker.tenantId, adminId: marker.adminId, userId: marker.userId, definitionId: marker.definitionId });
    if (scope.isCurrent(token)) setState({ status: 'success', pendingBody: null, success: parsed, conflictValue: null, errorCode: null });
  }, [scope, userId, handleConflict]);

  const submitValue = useCallback(async (body: StoredVariableValueBody): Promise<void> => {
    const token = scope.capture();
    if (!scope.isCurrent(token) || !token.tenantId || !token.adminId || !userId || !definitionId) return;
    if (busyRef.current === token) return;
    busyRef.current = token;
    try {
      setState((prev) => ({ ...prev, status: 'submitting', errorCode: null, conflictValue: null, success: null }));
      if (!environmentReady()) {
        setState({ status: 'blocked', pendingBody: null, success: null, conflictValue: null, errorCode: 'storage_unavailable' });
        return;
      }
      const bodyJson = JSON.stringify(body);
      const marker = buildValueMarker(token.tenantId, token.adminId, userId, definitionId, body.expected_version, bodyJson);
      // ПЕРЕД отправкой, синхронно — заморозка в localStorage; при ошибке
      // записи сама отправка не выполняется.
      const outcome = await navigator.locks.request(
        valueLockName(token.tenantId, token.adminId, userId, definitionId),
        async (): Promise<'claimed' | 'already_pending' | 'write_failed' | 'stale'> => {
          if (!scope.isCurrent(token)) return 'stale';
          const existing = readStoredValueAttempt(token.tenantId!, token.adminId!, userId, definitionId);
          if (existing.status !== 'empty') return 'already_pending';
          return writeStoredValueAttempt(marker) ? 'claimed' : 'write_failed';
        },
      ).catch((): 'write_failed' => 'write_failed');
      if (outcome === 'stale') return;
      if (outcome === 'write_failed') {
        setState({ status: 'blocked', pendingBody: null, success: null, conflictValue: null, errorCode: 'storage_unavailable' });
        return;
      }
      if (outcome === 'already_pending') { if (scope.isCurrent(token)) setState(computeSnapshot()); return; }
      notifyClientVarAttemptChanged({ kind: 'value', tenantId: token.tenantId, adminId: token.adminId, userId, definitionId });
      if (scope.isCurrent(token)) setState({ status: 'submitting', pendingBody: body, success: null, conflictValue: null, errorCode: null });
      await submit(token, marker);
    } finally {
      if (busyRef.current === token) busyRef.current = null;
    }
  }, [scope, userId, definitionId, submit, computeSnapshot]);

  const retry = useCallback(async (): Promise<void> => {
    const token = scope.capture();
    if (!scope.isCurrent(token) || !token.tenantId || !token.adminId || !userId || !definitionId) return;
    if (busyRef.current === token) return;
    busyRef.current = token;
    try {
      const read = readStoredValueAttempt(token.tenantId, token.adminId, userId, definitionId);
      if (read.status !== 'valid') { if (scope.isCurrent(token)) setState(computeSnapshot()); return; }
      setState({ status: 'submitting', pendingBody: JSON.parse(read.marker.bodyJson), success: null, conflictValue: null, errorCode: null });
      await submit(token, read.marker);
    } finally {
      if (busyRef.current === token) busyRef.current = null;
    }
  }, [scope, userId, definitionId, submit, computeSnapshot]);

  return { ...effective, submit: submitValue, retry };
}

// ── Персональная скидка (PATCH) ─────────────────────────────────

export type DiscountAttemptStatus = 'ready' | 'pending' | 'submitting' | 'blocked' | 'conflict' | 'success';

export interface DiscountAttemptState {
  status: DiscountAttemptStatus;
  pendingBody: StoredDiscountBody | null;
  success: PersonalDiscountSnapshot | null;
  conflictValue: PersonalDiscountSnapshot | null;
  errorCode: string | null;
}

const EMPTY_DISCOUNT_STATE: DiscountAttemptState = { status: 'ready', pendingBody: null, success: null, conflictValue: null, errorCode: null };

export function usePersonalDiscountAttempt(scope: ScenarioScope, userId: number) {
  const computeSnapshot = useCallback((): DiscountAttemptState => {
    const token = scope.capture();
    if (!scope.enabled || !token.tenantId || !token.adminId || !userId) return EMPTY_DISCOUNT_STATE;
    if (!environmentReady()) return { ...EMPTY_DISCOUNT_STATE, status: 'blocked', errorCode: 'storage_unavailable' };
    const read = readStoredDiscountAttempt(token.tenantId, token.adminId, userId);
    if (read.status === 'empty') return EMPTY_DISCOUNT_STATE;
    if (read.status === 'valid') return { ...EMPTY_DISCOUNT_STATE, status: 'pending', pendingBody: JSON.parse(read.marker.bodyJson) };
    return { ...EMPTY_DISCOUNT_STATE, status: 'blocked', errorCode: read.status === 'storage_error' ? 'storage_unavailable' : 'corrupt_marker' };
  }, [scope, userId]);

  const [state, setState] = useState<DiscountAttemptState>(() => computeSnapshot());
  const lastRef = useRef<{ scope: ScenarioScope; userId: number }>({ scope, userId });
  let effective = state;
  if (lastRef.current.scope !== scope || lastRef.current.userId !== userId) {
    lastRef.current = { scope, userId };
    effective = computeSnapshot();
    setState(effective);
  }

  const busyRef = useRef<ScenarioScopeToken | null>(null);

  useEffect(() => {
    const token = scope.capture();
    if (!scope.enabled || !token.tenantId || !token.adminId || !userId) return undefined;
    const tenantId = token.tenantId, adminId = token.adminId;
    const expectedKey = discountStorageKey(tenantId, adminId, userId);
    const refresh = () => {
      if (!scope.isCurrent(token)) return;
      setState((prev) => (prev.status === 'submitting' ? prev : computeSnapshot()));
    };
    const onStorage = (e: StorageEvent) => { if (e.key === null || e.key === expectedKey) refresh(); };
    const onCustom = (e: Event) => {
      const d = (e as CustomEvent<Record<string, unknown>>).detail;
      if (d && d.kind === 'discount' && d.tenantId === tenantId && d.adminId === adminId && d.userId === userId) refresh();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(CLIENT_VAR_ATTEMPT_EVENT, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CLIENT_VAR_ATTEMPT_EVENT, onCustom);
    };
  }, [scope, userId, computeSnapshot]);

  const handleConflict = useCallback(async (token: ScenarioScopeToken, marker: StoredDiscountMarker): Promise<void> => {
    let response;
    try {
      const requestConfig = scope.requestConfig(token);
      response = await getPersonalDiscount(userId, requestConfig);
    } catch {
      if (scope.isCurrent(token)) setState({ status: 'pending', pendingBody: JSON.parse(marker.bodyJson), success: null, conflictValue: null, errorCode: 'unknown_retry_needed' });
      return;
    }
    if (!scope.isCurrent(token)) return;
    const fresh = parsePersonalDiscount(response);
    if (!fresh || fresh.version <= marker.expectedVersion) {
      setState({ status: 'pending', pendingBody: JSON.parse(marker.bodyJson), success: null, conflictValue: null, errorCode: 'unknown_retry_needed' });
      return;
    }
    try {
      await navigator.locks.request(discountLockName(marker.tenantId, marker.adminId, marker.userId), async () => {
        if (!scope.isCurrent(token)) return;
        const current = readStoredDiscountAttempt(marker.tenantId, marker.adminId, marker.userId);
        if (current.status === 'valid' && discountMarkersMatch(current.marker, marker)) {
          removeStoredDiscountAttempt(marker.tenantId, marker.adminId, marker.userId);
        }
      });
    } catch { /* best-effort */ }
    notifyClientVarAttemptChanged({ kind: 'discount', tenantId: marker.tenantId, adminId: marker.adminId, userId: marker.userId });
    if (scope.isCurrent(token)) setState({ status: 'conflict', pendingBody: null, success: null, conflictValue: fresh, errorCode: null });
  }, [scope, userId]);

  const submit = useCallback(async (token: ScenarioScopeToken, marker: StoredDiscountMarker): Promise<void> => {
    let response;
    try {
      const requestConfig = scope.requestConfig(token);
      const body = JSON.parse(marker.bodyJson) as StoredDiscountBody;
      response = await setPersonalDiscount(userId, body.personal_discount, body.expected_version, requestConfig);
    } catch (error) {
      if (!scope.isCurrent(token)) return;
      if (axios.isAxiosError(error) && error.response?.status === 409) { await handleConflict(token, marker); return; }
      setState({ status: 'pending', pendingBody: JSON.parse(marker.bodyJson), success: null, conflictValue: null, errorCode: 'unknown_retry_needed' });
      return;
    }
    if (!scope.isCurrent(token)) return;
    const requestBody = JSON.parse(marker.bodyJson) as StoredDiscountBody;
    const parsed = parsePersonalDiscountWriteAck(response);
    const ackValid = parsed !== null && parsed.version === marker.expectedVersion + 1 && parsed.personalDiscount === requestBody.personal_discount;
    if (!ackValid) {
      setState({ status: 'pending', pendingBody: requestBody, success: null, conflictValue: null, errorCode: 'invalid_response' });
      return;
    }
    try {
      await navigator.locks.request(discountLockName(marker.tenantId, marker.adminId, marker.userId), async () => {
        if (!scope.isCurrent(token)) return;
        const current = readStoredDiscountAttempt(marker.tenantId, marker.adminId, marker.userId);
        if (current.status === 'valid' && discountMarkersMatch(current.marker, marker)) {
          removeStoredDiscountAttempt(marker.tenantId, marker.adminId, marker.userId);
        }
      });
    } catch { /* best-effort */ }
    notifyClientVarAttemptChanged({ kind: 'discount', tenantId: marker.tenantId, adminId: marker.adminId, userId: marker.userId });
    if (scope.isCurrent(token)) setState({ status: 'success', pendingBody: null, success: parsed, conflictValue: null, errorCode: null });
  }, [scope, userId, handleConflict]);

  const submitDiscount = useCallback(async (body: StoredDiscountBody): Promise<void> => {
    const token = scope.capture();
    if (!scope.isCurrent(token) || !token.tenantId || !token.adminId || !userId) return;
    if (busyRef.current === token) return;
    busyRef.current = token;
    try {
      setState((prev) => ({ ...prev, status: 'submitting', errorCode: null, conflictValue: null, success: null }));
      if (!environmentReady()) {
        setState({ status: 'blocked', pendingBody: null, success: null, conflictValue: null, errorCode: 'storage_unavailable' });
        return;
      }
      const bodyJson = JSON.stringify(body);
      const marker = buildDiscountMarker(token.tenantId, token.adminId, userId, body.expected_version, bodyJson);
      const outcome = await navigator.locks.request(
        discountLockName(token.tenantId, token.adminId, userId),
        async (): Promise<'claimed' | 'already_pending' | 'write_failed' | 'stale'> => {
          if (!scope.isCurrent(token)) return 'stale';
          const existing = readStoredDiscountAttempt(token.tenantId!, token.adminId!, userId);
          if (existing.status !== 'empty') return 'already_pending';
          return writeStoredDiscountAttempt(marker) ? 'claimed' : 'write_failed';
        },
      ).catch((): 'write_failed' => 'write_failed');
      if (outcome === 'stale') return;
      if (outcome === 'write_failed') {
        setState({ status: 'blocked', pendingBody: null, success: null, conflictValue: null, errorCode: 'storage_unavailable' });
        return;
      }
      if (outcome === 'already_pending') { if (scope.isCurrent(token)) setState(computeSnapshot()); return; }
      notifyClientVarAttemptChanged({ kind: 'discount', tenantId: token.tenantId, adminId: token.adminId, userId });
      if (scope.isCurrent(token)) setState({ status: 'submitting', pendingBody: body, success: null, conflictValue: null, errorCode: null });
      await submit(token, marker);
    } finally {
      if (busyRef.current === token) busyRef.current = null;
    }
  }, [scope, userId, submit, computeSnapshot]);

  const retry = useCallback(async (): Promise<void> => {
    const token = scope.capture();
    if (!scope.isCurrent(token) || !token.tenantId || !token.adminId || !userId) return;
    if (busyRef.current === token) return;
    busyRef.current = token;
    try {
      const read = readStoredDiscountAttempt(token.tenantId, token.adminId, userId);
      if (read.status !== 'valid') { if (scope.isCurrent(token)) setState(computeSnapshot()); return; }
      setState({ status: 'submitting', pendingBody: JSON.parse(read.marker.bodyJson), success: null, conflictValue: null, errorCode: null });
      await submit(token, read.marker);
    } finally {
      if (busyRef.current === token) busyRef.current = null;
    }
  }, [scope, userId, submit, computeSnapshot]);

  return { ...effective, submit: submitDiscount, retry };
}

// ── Создание определения переменной (POST) ──────────────────────

export type DefCreateStatus = 'ready' | 'pending' | 'submitting' | 'blocked';

export interface DefCreateAttemptState {
  status: DefCreateStatus;
  pendingBody: StoredDefinitionCreateBody | null;
  created: ClientVariableDefinition | null;
  conflictDefinition: DefinitionEcho | null;
  errorCode: string | null;
}

const EMPTY_DEF_CREATE_STATE: DefCreateAttemptState = { status: 'ready', pendingBody: null, created: null, conflictDefinition: null, errorCode: null };

export function useVariableDefinitionCreateAttempt(scope: ScenarioScope, key: string) {
  const computeSnapshot = useCallback((): DefCreateAttemptState => {
    const token = scope.capture();
    if (!scope.enabled || !token.tenantId || !token.adminId || !key) return EMPTY_DEF_CREATE_STATE;
    if (!environmentReady()) return { ...EMPTY_DEF_CREATE_STATE, status: 'blocked', errorCode: 'storage_unavailable' };
    const read = readStoredDefinitionCreateAttempt(token.tenantId, token.adminId, key);
    if (read.status === 'empty') return EMPTY_DEF_CREATE_STATE;
    if (read.status === 'valid') return { ...EMPTY_DEF_CREATE_STATE, status: 'pending', pendingBody: JSON.parse(read.marker.bodyJson) };
    return { ...EMPTY_DEF_CREATE_STATE, status: 'blocked', errorCode: read.status === 'storage_error' ? 'storage_unavailable' : 'corrupt_marker' };
  }, [scope, key]);

  const [state, setState] = useState<DefCreateAttemptState>(() => computeSnapshot());
  const lastRef = useRef<{ scope: ScenarioScope; key: string }>({ scope, key });
  let effective = state;
  if (lastRef.current.scope !== scope || lastRef.current.key !== key) {
    lastRef.current = { scope, key };
    effective = computeSnapshot();
    setState(effective);
  }

  const busyRef = useRef<ScenarioScopeToken | null>(null);

  useEffect(() => {
    const token = scope.capture();
    if (!scope.enabled || !token.tenantId || !token.adminId || !key) return undefined;
    const tenantId = token.tenantId, adminId = token.adminId;
    const expectedKey = definitionCreateStorageKey(tenantId, adminId, key);
    const refresh = () => {
      if (!scope.isCurrent(token)) return;
      setState((prev) => (prev.status === 'submitting' ? prev : computeSnapshot()));
    };
    const onStorage = (e: StorageEvent) => { if (e.key === null || e.key === expectedKey) refresh(); };
    const onCustom = (e: Event) => {
      const d = (e as CustomEvent<Record<string, unknown>>).detail;
      if (d && d.kind === 'definition_create' && d.tenantId === tenantId && d.adminId === adminId && d.key === key) refresh();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener(CLIENT_VAR_ATTEMPT_EVENT, onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CLIENT_VAR_ATTEMPT_EVENT, onCustom);
    };
  }, [scope, key, computeSnapshot]);

  // Нет ручки по ключу — сверка только по свежему списку определений.
  // Найдено с совпадающим контрактом — информативно (не "это моё", не
  // усыновляем чужую запись). Найдено с другим типом/лейблом/choices —
  // явное предупреждение о несовпадении. Не найдено — unknown_retry_needed.
  const reconcileByKey = useCallback(async (token: ScenarioScopeToken, key_: string): Promise<DefinitionEcho | null> => {
    try {
      const requestConfig = scope.requestConfig(token);
      const defs = await listVariableDefinitions(requestConfig);
      const match = defs.find((d) => d.key === key_);
      if (!match) return null;
      return { key: match.key, label: match.label, valueType: match.value_type, enumChoices: match.enum_choices };
    } catch {
      return null;
    }
  }, [scope]);

  const submit = useCallback(async (token: ScenarioScopeToken, marker: StoredDefinitionCreateMarker): Promise<void> => {
    let response;
    try {
      const requestConfig = scope.requestConfig(token);
      const body = JSON.parse(marker.bodyJson) as StoredDefinitionCreateBody;
      response = await createVariableDefinition(body, requestConfig);
    } catch (error) {
      if (!scope.isCurrent(token)) return;
      const is409 = axios.isAxiosError(error) && error.response?.status === 409;
      const conflictDefinition = await reconcileByKey(token, marker.key);
      if (!scope.isCurrent(token)) return;
      setState({
        status: 'pending', pendingBody: JSON.parse(marker.bodyJson), created: null, conflictDefinition,
        errorCode: is409 ? (conflictDefinition ? 'duplicate_key' : 'unknown_retry_needed') : 'unknown_retry_needed',
      });
      return;
    }
    if (!scope.isCurrent(token)) return;
    const frozenBody = JSON.parse(marker.bodyJson) as StoredDefinitionCreateBody;
    const echo = parseDefinitionEcho(response);
    const ackValid = echo !== null && echo.key === frozenBody.key && echo.label === frozenBody.label
      && echo.valueType === frozenBody.value_type && enumChoicesEqual(echo.enumChoices, frozenBody.enum_choices);
    if (!ackValid) {
      setState({ status: 'pending', pendingBody: frozenBody, created: null, conflictDefinition: null, errorCode: 'invalid_response' });
      return;
    }
    try {
      await navigator.locks.request(definitionCreateLockName(marker.tenantId, marker.adminId, marker.key), async () => {
        if (!scope.isCurrent(token)) return;
        const current = readStoredDefinitionCreateAttempt(marker.tenantId, marker.adminId, marker.key);
        if (current.status === 'valid' && definitionCreateMarkersMatch(current.marker, marker)) {
          removeStoredDefinitionCreateAttempt(marker.tenantId, marker.adminId, marker.key);
        }
      });
    } catch { /* best-effort */ }
    notifyClientVarAttemptChanged({ kind: 'definition_create', tenantId: marker.tenantId, adminId: marker.adminId, key: marker.key });
    if (scope.isCurrent(token)) {
      setState({ status: 'ready', pendingBody: null, created: response, conflictDefinition: null, errorCode: null });
    }
  }, [scope, reconcileByKey]);

  const create = useCallback(async (body: StoredDefinitionCreateBody): Promise<void> => {
    const token = scope.capture();
    if (!scope.isCurrent(token) || !token.tenantId || !token.adminId || !key) return;
    if (busyRef.current === token) return;
    busyRef.current = token;
    try {
      setState((prev) => ({ ...prev, status: 'submitting', errorCode: null, conflictDefinition: null }));
      if (!environmentReady()) {
        setState({ status: 'blocked', pendingBody: null, created: null, conflictDefinition: null, errorCode: 'storage_unavailable' });
        return;
      }
      const bodyJson = JSON.stringify(body);
      const marker = buildDefinitionCreateMarker(token.tenantId, token.adminId, key, bodyJson);
      const outcome = await navigator.locks.request(
        definitionCreateLockName(token.tenantId, token.adminId, key),
        async (): Promise<'claimed' | 'already_pending' | 'write_failed' | 'stale'> => {
          if (!scope.isCurrent(token)) return 'stale';
          const existing = readStoredDefinitionCreateAttempt(token.tenantId!, token.adminId!, key);
          if (existing.status !== 'empty') return 'already_pending';
          return writeStoredDefinitionCreateAttempt(marker) ? 'claimed' : 'write_failed';
        },
      ).catch((): 'write_failed' => 'write_failed');
      if (outcome === 'stale') return;
      if (outcome === 'write_failed') {
        setState({ status: 'blocked', pendingBody: null, created: null, conflictDefinition: null, errorCode: 'storage_unavailable' });
        return;
      }
      if (outcome === 'already_pending') { if (scope.isCurrent(token)) setState(computeSnapshot()); return; }
      notifyClientVarAttemptChanged({ kind: 'definition_create', tenantId: token.tenantId, adminId: token.adminId, key });
      if (scope.isCurrent(token)) setState({ status: 'submitting', pendingBody: body, created: null, conflictDefinition: null, errorCode: null });
      await submit(token, marker);
    } finally {
      if (busyRef.current === token) busyRef.current = null;
    }
  }, [scope, key, submit, computeSnapshot]);

  const retry = useCallback(async (): Promise<void> => {
    const token = scope.capture();
    if (!scope.isCurrent(token) || !token.tenantId || !token.adminId || !key) return;
    if (busyRef.current === token) return;
    busyRef.current = token;
    try {
      const read = readStoredDefinitionCreateAttempt(token.tenantId, token.adminId, key);
      if (read.status !== 'valid') { if (scope.isCurrent(token)) setState(computeSnapshot()); return; }
      setState({ status: 'submitting', pendingBody: JSON.parse(read.marker.bodyJson), created: null, conflictDefinition: null, errorCode: null });
      await submit(token, read.marker);
    } finally {
      if (busyRef.current === token) busyRef.current = null;
    }
  }, [scope, key, submit, computeSnapshot]);

  return { ...effective, create, retry };
}
